import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { ShieldCheck, ChevronDown, ChevronUp, Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const relColor = (s) =>
  s == null ? "text-muted-foreground" : s >= 85 ? "text-emerald-600 dark:text-emerald-400"
    : s >= 65 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";
const relDot = (s) =>
  s == null ? "bg-muted-foreground" : s >= 85 ? "bg-emerald-500" : s >= 65 ? "bg-amber-500" : "bg-red-500";
const confColor = (s) =>
  s == null ? "text-muted-foreground" : s >= 80 ? "text-emerald-600 dark:text-emerald-400"
    : s >= 55 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";

const reasonStyle = {
  price: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  sold_out: "bg-red-500/10 text-red-700 dark:text-red-400",
  min_stay: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
  restriction: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  uncertain: "bg-secondary text-muted-foreground",
  not_detectable: "bg-secondary text-muted-foreground",
};

export default function CompetitorReliability({ propId, lang = "it", refreshKey }) {
  const L = (it, en) => (lang === "it" ? it : en);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!propId) return;
    setLoading(true);
    api.get(`/properties/${propId}/competitor-reliability?lang=${lang}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ has_data: false, competitors: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(load, [propId, lang, refreshKey]);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post(`/properties/${propId}/competitor-profiles/recompute`);
      toast.success(L("Profili ricalcolati dagli snapshot", "Profiles recomputed from snapshots"));
      load();
    } catch { toast.error(L("Errore nel ricalcolo", "Recompute failed")); }
    finally { setBusy(false); }
  };

  if (loading) return null;
  if (!data || !data.has_data || !(data.competitors || []).length) return null;

  const Bar = ({ pct, cls }) => <div className={cls} style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }} />;

  return (
    <motion.div data-testid="competitor-reliability" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="md:col-span-4 bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">{L("Affidabilità Competitor", "Competitor Reliability")}</h3>
        </div>
        <Button variant="outline" size="sm" onClick={recompute} disabled={busy} data-testid="reliability-recompute">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> {L("Aggiorna profili", "Refresh profiles")}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {L("Revenue AI impara nel tempo dagli snapshot giornalieri: distingue prezzo rilevato, sold out reale e restrizioni (minimum stay/CTA). Non fa chiamate aggiuntive.",
           "Revenue AI learns over time from daily snapshots: it tells apart detected price, real sold-out and restrictions (minimum stay/CTA). No extra calls.")}
      </p>

      <div className="space-y-2" data-testid="reliability-list">
        {data.competitors.map((c) => {
          const isOpen = open[c.competitor_id];
          const td = c.today;
          return (
            <div key={c.competitor_id} className="border border-border rounded-lg overflow-hidden">
              <button data-testid={`reliability-row`} onClick={() => setOpen((o) => ({ ...o, [c.competitor_id]: !o[c.competitor_id] }))}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-secondary/40 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap text-xs">
                    <span className={`flex items-center gap-1.5 font-bold ${relColor(c.reliability_score)}`}>
                      <span className={`w-2 h-2 rounded-full ${relDot(c.reliability_score)}`} />
                      {L("Affidabilità", "Reliability")} {c.reliability_score ?? "–"}/100
                    </span>
                    {td ? (
                      <span className={`font-semibold ${confColor(td.status_confidence)}`}>
                        {L("Confidence oggi", "Confidence today")} {td.status_confidence}%
                      </span>
                    ) : null}
                    <span className="text-muted-foreground">{c.snapshots_used} snapshot</span>
                  </div>
                </div>
                {td ? (
                  <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-medium ${reasonStyle[td.reason] || reasonStyle.uncertain}`}>
                    {td.reason_label}{td.reason === "sold_out" ? ` ${td.sold_out_probability}%` : td.reason === "min_stay" || td.reason === "restriction" ? ` ${td.restriction_probability}%` : ""}
                  </span>
                ) : null}
                {isOpen ? <ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />}
              </button>

              {isOpen ? (
                <div className="px-4 pb-4 pt-1 border-t border-border bg-secondary/20 text-sm space-y-3" data-testid="reliability-detail">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold mb-1.5">{L("Comportamento storico", "Historical behaviour")}</div>
                    <div className="h-3 rounded-full overflow-hidden flex bg-muted mb-1.5">
                      <Bar pct={c.pct_price_detected} cls="bg-emerald-500" />
                      <Bar pct={c.pct_unavailable} cls="bg-amber-500" />
                      <Bar pct={c.pct_not_detectable} cls="bg-muted-foreground/40" />
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <span className="text-emerald-600 dark:text-emerald-400">{L("Prezzi rilevati", "Prices detected")}: <b>{c.pct_price_detected}%</b></span>
                      <span className="text-amber-600 dark:text-amber-400">{L("Non disponibile", "Unavailable")}: <b>{c.pct_unavailable}%</b></span>
                      <span className="text-muted-foreground">{L("Non rilevabile", "Not detectable")}: <b>{c.pct_not_detectable}%</b></span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <span>{L("Quota sold out reali", "Real sold-out share")}: <b>{Math.round((c.soldout_rate || 0) * 100)}%</b></span>
                    <span>{L("Quota restrizioni", "Restriction share")}: <b>{Math.round((c.restriction_rate || 0) * 100)}%</b></span>
                  </div>
                  {(c.patterns || []).length ? (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold mb-1.5 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {L("Pattern rilevati", "Detected patterns")}</div>
                      <ul className="space-y-1">
                        {c.patterns.map((p) => <li key={p.key} className="text-sm">• {p.label}</li>)}
                      </ul>
                    </div>
                  ) : null}
                  {c.updated_at ? (
                    <div className="text-[11px] text-muted-foreground">
                      {L("Aggiornato", "Updated")}: {new Date(c.updated_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")} · {c.snapshots_used} snapshot
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
